import { Avatar, Menu, MenuItem, useBoolean, useBreakpoints } from "@aurem/ui";
import ROUTES from "const/routes";
import { useNavigation } from "hooks/navigate";
import { useTermsDocumentUrl } from "hooks/useTermsDocumentUrl";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useMemo } from "react";
import { useSignOut } from "utils/auth";
import { useUserData } from "../../graphql/user";
import { useCurrentApp } from "../../hooks/useCurrentApp";
import { RolePicker } from "./RolePicker";
import { UserDrawer } from "./UserDrawer";

export const UserDropdown = () => {
  const { t, language } = useI18n();
  const navigate = useNavigation();
  const signOut = useSignOut();
  const { user } = useUserData();
  const name = user?.profile?.name;
  const email = user?.email;
  const termsUrl = useTermsDocumentUrl(language);

  const { isMobile } = useBreakpoints();

  const {
    value: showUserDrawer,
    setTrue: openUserDrawer,
    setFalse: closeUserDrawer,
  } = useBoolean(false);

  const currentApp = useCurrentApp();
  const isAdminRoute = currentApp === "admin";

  const handleSignOut = useCallback(() => {
    void signOut();
  }, [signOut]);

  const options: MenuItem[] = useMemo(() => {
    const options: (MenuItem | undefined)[] = [
      user
        ? {
            type: "element",
            element: <RolePicker user={user} />,
          }
        : undefined,
      {
        type: "divider",
        className: "-mx-1",
      },
      {
        id: "personal-settings",
        text: t("apps.admin.navBar.personalSettings"),
      },
      !isAdminRoute && termsUrl
        ? {
            id: "terms-and-conditions",
            text: t("apps.employee.navigation.termsAndConditions"),
          }
        : undefined,
      {
        id: "sign-out",
        text: t("apps.admin.common.signOut"),
      },
    ];

    return options.filter((option) => !!option);
  }, [t, isAdminRoute, termsUrl, user]);

  const onSelect = useCallback(
    (id: string) => {
      if (id === "personal-settings") {
        if (isAdminRoute) {
          void navigate(`/${ROUTES.PROFILE}`);
        } else {
          void navigate(`/${language}/employee/settings/account-details`);
        }
      } else if (id === "terms-and-conditions") {
        if (termsUrl) {
          window.open(termsUrl, "_blank", "noopener,noreferrer");
        }
      } else if (id === "sign-out") {
        handleSignOut();
      }
    },
    [navigate, handleSignOut, language, isAdminRoute, termsUrl],
  );

  if (!email) return null;

  const avatarJSX = (
    <Avatar
      text={name ?? email}
      className="rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 cursor-pointer"
      style={{ outlineOffset: "1px" }}
    />
  );

  if (isMobile) {
    return (
      <>
        <button
          className="bg-transparent border-none outline-none"
          onClick={openUserDrawer}
        >
          {avatarJSX}
        </button>
        <UserDrawer open={showUserDrawer} onClose={closeUserDrawer} />
      </>
    );
  }

  return (
    <div
      className="personal-settings-tour-step" /* eslint-disable-line tailwindcss/no-custom-classname */
    >
      <Menu options={options} onSelect={onSelect} minWidth={240} maxWidth={240}>
        {avatarJSX}
      </Menu>
    </div>
  );
};
